import React, { useState } from 'react';
import { ShieldCheck, LogOut, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

/**
 * Activación obligatoria de 2FA.
 *
 * Se muestra a los roles que lo exigen (admin, gerente, seguridad) cuando entran sin
 * segundo factor. No es una sugerencia: el token que tienen guardado solo sirve para
 * los endpoints de activación — el resto lo rechaza el backend, así que esta pantalla
 * refleja una restricción real, no la crea.
 *
 * Reusa los estilos de la pantalla de login para no introducir CSS nuevo.
 */
export default function MfaEnrollment() {
  const { user, logout, completeMfaEnrollment } = useAuth();
  const [step, setStep]       = useState('intro');   // intro | qr
  const [qr, setQr]           = useState('');
  const [secret, setSecret]   = useState('');
  const [code, setCode]       = useState('');
  const [error, setError]     = useState('');
  const [loading, setLoading] = useState(false);

  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('token')}`,
  });

  const startSetup = async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_URL}/auth/2fa/setup`, { method: 'POST', headers: authHeaders() });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'No se pudo generar el código.');
      setQr(d.qr); setSecret(d.secret); setStep('qr');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const confirm = async (e) => {
    e.preventDefault();
    const clean = code.replace(/\s/g, '');
    if (clean.length < 6) { setError('Ingresá los 6 dígitos.'); return; }
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API_URL}/auth/2fa/enable`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ code: clean }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Código inválido.');
      // El backend devuelve el token completo al activar: se reemplaza el restringido
      // y la aplicación queda liberada sin volver a pedir credenciales.
      if (d.token) completeMfaEnrollment(d.token, d.user);
      else window.location.reload();
    } catch (e) {
      setError(e.message);
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card" style={{ maxWidth: 460 }}>
        <div style={{ textAlign: 'center', marginBottom: 18 }}>
          <ShieldCheck size={40} style={{ color: 'var(--accent)' }} />
          <h2 style={{ margin: '10px 0 4px', fontSize: 20, fontWeight: 700, color: 'var(--ink)' }}>
            Activá la verificación en dos pasos
          </h2>
          <p style={{ margin: 0, fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.5 }}>
            Tu rol <strong>{user?.role}</strong> requiere un segundo factor para acceder.
            Es un paso único y toma menos de un minuto.
          </p>
        </div>

        {step === 'intro' && (
          <>
            <ol style={{ fontSize: 13.5, color: 'var(--ink-2)', lineHeight: 1.8, paddingLeft: 20, margin: '0 0 18px' }}>
              <li>Instalá una app de autenticación (Google Authenticator, Microsoft Authenticator, 1Password…).</li>
              <li>Escaneá el código QR que aparece en el siguiente paso.</li>
              <li>Ingresá los 6 dígitos para confirmar.</li>
            </ol>
            <button className="btn btn-primary" onClick={startSetup} disabled={loading}
              style={{ width: '100%', justifyContent: 'center', display: 'flex', alignItems: 'center', gap: 8 }}>
              {loading ? <><Loader2 size={16} className="spin" /> Generando…</> : 'Comenzar'}
            </button>
          </>
        )}

        {step === 'qr' && (
          <form onSubmit={confirm}>
            <div style={{ textAlign: 'center', marginBottom: 14 }}>
              <img src={qr} alt="Código QR para la app de autenticación"
                style={{ width: 190, height: 190, borderRadius: 12, border: '1px solid var(--border)' }} />
              <p style={{ fontSize: 12, color: 'var(--ink-2)', margin: '10px 0 4px' }}>
                ¿No podés escanear? Cargá esta clave a mano:
              </p>
              <code style={{ fontSize: 12, background: 'var(--bg)', padding: '5px 9px', borderRadius: 6,
                             wordBreak: 'break-all', display: 'inline-block', color: 'var(--ink-2)' }}>
                {secret}
              </code>
            </div>
            <input
              type="text" inputMode="numeric" autoComplete="one-time-code" maxLength={7}
              value={code} onChange={(e) => setCode(e.target.value)}
              placeholder="000000" autoFocus
              style={{ width: '100%', padding: '11px 14px', fontSize: 21, letterSpacing: 7,
                       textAlign: 'center', border: '1px solid var(--border-2)', borderRadius: 10, marginBottom: 12 }}
            />
            <button type="submit" className="btn btn-primary" disabled={loading}
              style={{ width: '100%', justifyContent: 'center', display: 'flex', alignItems: 'center', gap: 8 }}>
              {loading ? <><Loader2 size={16} className="spin" /> Verificando…</> : 'Activar y continuar'}
            </button>
          </form>
        )}

        {error && (
          <div style={{ marginTop: 12, padding: '9px 12px', background: 'var(--red-dim)', border: '1px solid var(--red-dim)',
                        borderRadius: 8, color: 'var(--red)', fontSize: 13 }}>
            {error}
          </div>
        )}

        <button onClick={logout}
          style={{ marginTop: 16, width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                   color: 'var(--ink-3)', fontSize: 12.5, display: 'flex', alignItems: 'center',
                   justifyContent: 'center', gap: 6 }}>
          <LogOut size={13} /> Cerrar sesión
        </button>
      </div>
    </div>
  );
}
